/**
 * Single-flight + latest-wins request guard.
 *
 * Organization list stores (Positions, Roles, Responsibilities, …) are loaded
 * from more than one call site within the same view-state lifecycle (an
 * `onMounted` hook plus an immediate watcher, or a parent tab plus the table it
 * renders). Each call used to issue its own GET and its own CORS `OPTIONS`
 * preflight — hence the duplicate requests captured in ER-11705.
 *
 * `createLatestRequestGuard` collapses concurrent calls that share a key into a
 * single in-flight promise (single-flight dedupe), and lets a store discard a
 * response that was superseded by a newer call (latest-wins), so the newest
 * filter always wins even when an earlier, slower request lands last.
 */

/** Recursively sort object keys so equal-but-differently-ordered payloads match. */
function sortValueDeep(value) {
  if (Array.isArray(value)) {
    return value.map(sortValueDeep);
  }

  if (value !== null && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortValueDeep(value[key]);
        return acc;
      }, {});
  }

  return value;
}

/**
 * Canonical string key for a request. Ordering the object keys prevents the
 * server-vs-request parameter-order mismatch noted in ER-11705 from defeating
 * the dedupe.
 */
export function stableKey(value) {
  return JSON.stringify(sortValueDeep(value == null ? null : value));
}

export function createLatestRequestGuard({ ttlMs = 0, now = Date.now } = {}) {
  let latest = null;

  function isJoinable(entry) {
    if (!entry.settled) {
      return true;
    }
    return ttlMs > 0 && !entry.failed && now() - entry.settledAt < ttlMs;
  }

  /**
   * Start (or join) a request.
   * - Same key as the latest request and still in flight -> returns that entry
   *   unchanged, so the task does not run again.
   * - With `ttlMs`, a request that resolved successfully stays joinable for that
   *   long, so sequential callers of the same view load share one response.
   * - `fresh: true` skips the join (e.g. a reload right after a write, which must
   *   not share a read that began before the write landed).
   * - Anything else starts `task` and marks the previous entry superseded.
   */
  function start(key, task, { fresh = false } = {}) {
    if (!fresh && latest && latest.key === key && isJoinable(latest)) {
      return latest;
    }

    if (latest) {
      latest.superseded = true;
    }

    const entry = {
      key,
      promise: null,
      settled: false,
      failed: false,
      settledAt: 0,
      superseded: false,
      isCurrent: () => !entry.superseded,
    };

    latest = entry;

    // `task` is expected to be an async function (or return a promise). A
    // synchronous throw (e.g. JSON.parse of a corrupt localStorage value) is
    // turned into a rejection so it flows to the store's existing try/catch.
    let promise;
    try {
      promise = task(entry);
    } catch (err) {
      promise = Promise.reject(err);
    }
    entry.promise = Promise.resolve(promise);

    // Registered before the caller can await, so `settled` flips ahead of any
    // caller continuation: a call made right after an awaited one starts fresh.
    const markSettled = (failed) => () => {
      entry.settled = true;
      entry.failed = failed;
      entry.settledAt = now();
    };
    entry.promise.then(markSettled(false), markSettled(true));

    return entry;
  }

  /** Supersede any in-flight request (e.g. on account switch / state reset). */
  function reset() {
    if (latest) {
      latest.superseded = true;
    }
    latest = null;
  }

  return { start, reset };
}
